import React, { useCallback, useEffect, useState } from 'react'
import api, { AdminCacheKey, AdminCacheRenderResponse } from './api-client'

const formatTtl = (ttlSeconds: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ttlSeconds))
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const pad = (n: number) => String(n).padStart(2, '0')

  let output = ''
  if (days > 0) output += `${pad(days)}D`
  if (days > 0 || hours > 0) output += `${pad(hours)}H`
  if (days > 0 || hours > 0 || minutes > 0) output += `${pad(minutes)}M`
  output += `${pad(seconds)}S`

  return output
}

const CacheManager: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [busyKey, setBusyKey] = useState('')
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [error, setError] = useState('')
  const [keys, setKeys] = useState<AdminCacheKey[]>([])
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [rendered, setRendered] = useState<AdminCacheRenderResponse | null>(null)
  const [renderModalOpen, setRenderModalOpen] = useState(false)

  const load = useCallback(async (query: string) => {
    setLoading(true)
    setError('')
    try {
      const res = await api.getCacheKeys(query, 200)
      setKeys(Array.isArray(res?.keys) ? res.keys : [])
    } catch (ex) {
      setError(ex?.message || 'Failed to load cache keys')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search)
    }, 500)
    return () => window.clearTimeout(timeoutId)
  }, [search])

  useEffect(() => {
    void load(debouncedSearch)
  }, [debouncedSearch, load])

  useEffect(() => {
    setSelectedKeys((prev) => {
      const visible = new Set(keys.map((k) => k.key))
      return prev.filter((k) => visible.has(k))
    })
  }, [keys])

  const handleRender = async (key: string) => {
    setBusyKey(key)
    setError('')
    try {
      const res = await api.renderCacheKey(key)
      setRendered(res)
      setRenderModalOpen(true)
    } catch (ex) {
      setError(ex?.message || 'Failed to render cache value')
    } finally {
      setBusyKey('')
    }
  }

  const renderedPayload = (() => {
    if (!rendered) return ''
    try {
      return JSON.stringify(rendered.value, null, 2)
    } catch (_) {
      return String(rendered.value || '')
    }
  })()

  const handleCopyPayload = async () => {
    if (!renderedPayload) return
    try {
      await navigator.clipboard.writeText(renderedPayload)
    } catch (_) {
      window.alert('Unable to copy payload to clipboard.')
    }
  }

  const allSelected = keys.length > 0 && selectedKeys.length === keys.length

  const handleToggleSelect = (key: string, checked: boolean) => {
    setSelectedKeys((prev) => {
      if (checked) return prev.includes(key) ? prev : [...prev, key]
      return prev.filter((k) => k !== key)
    })
  }

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedKeys([])
      return
    }
    setSelectedKeys(keys.map((k) => k.key))
  }

  const handleDeleteSelected = async () => {
    if (!selectedKeys.length) return
    const confirmed = window.confirm(
      `Delete ${selectedKeys.length} selected cache key(s)? This action cannot be undone.`
    )
    if (!confirmed) return

    setBulkDeleting(true)
    setError('')
    try {
      await Promise.all(selectedKeys.map((key) => api.deleteCacheKey(key)))
      if (rendered?.key && selectedKeys.includes(rendered.key)) setRendered(null)
      setSelectedKeys([])
      await load(debouncedSearch)
    } catch (ex) {
      setError(ex?.message || 'Failed to delete selected cache keys')
    } finally {
      setBulkDeleting(false)
    }
  }

  return (
    <div className="container mt-5">
      <h1>Cache Busting</h1>
      <p className="text-muted">
        Admin-only cache inspector. List keys, render cached value, or delete keys.
      </p>

      <div className="form-inline-row" style={{ gap: '0.5em', alignItems: 'center' }}>
        <input
          type="text"
          className="form-control"
          placeholder="Search cache keys"
          value={search}
          onChange={(ev) => setSearch(ev.target.value)}
          style={{ maxWidth: 360 }}
        />
      </div>

      <div
        className="form-inline-row mt-2"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            className="btn btn-outline-secondary"
            type="button"
            disabled={loading || bulkDeleting || keys.length === 0}
            onClick={handleSelectAll}
          >
            {allSelected ? 'Unselect all' : 'Select all'}
          </button>
          <button
            className="btn btn-danger"
            type="button"
            disabled={loading || bulkDeleting || selectedKeys.length === 0}
            onClick={() => void handleDeleteSelected()}
          >
            Delete selected ({selectedKeys.length})
          </button>
        </div>
        <div>
          <button
            className="btn btn-primary"
            type="button"
            disabled={loading || bulkDeleting}
            onClick={() => void load(search)}
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="text-muted mt-2" style={{ fontSize: 12 }}>
        Search is debounced by 0.5 seconds.
      </div>

      {error ? <div className="alert alert-danger mt-3">{error}</div> : null}

      {loading ? (
        <div className="d-flex justify-content-center mt-5">
          <div className="spinner-border" />
        </div>
      ) : (
        <table className="table mt-3">
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <input
                  type="checkbox"
                  aria-label="Select all cache keys"
                  checked={allSelected}
                  onChange={handleSelectAll}
                />
              </th>
              <th>Key</th>
              <th>Type</th>
              <th>TTL</th>
              <th>Size (bytes)</th>
              <th style={{ width: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.key}>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Select ${k.key}`}
                    checked={selectedKeys.includes(k.key)}
                    onChange={(ev) => handleToggleSelect(k.key, ev.target.checked)}
                  />
                </td>
                <td style={{ wordBreak: 'break-all' }}>{k.key}</td>
                <td>{k.type}</td>
                <td>{k.ttlSeconds === null ? 'No expiry' : formatTtl(k.ttlSeconds)}</td>
                <td>{typeof k.sizeBytes === 'number' ? k.sizeBytes : 'N/A'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      type="button"
                      disabled={busyKey === k.key || bulkDeleting}
                      onClick={() => void handleRender(k.key)}
                    >
                      Render
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {keys.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-muted">No cache keys found.</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      )}

      {renderModalOpen && rendered ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: 16,
          }}
          onClick={(ev) => {
            if (ev.target === ev.currentTarget) setRenderModalOpen(false)
          }}
        >
          <div
            style={{
              background: '#fff',
              width: '100%',
              maxWidth: 900,
              maxHeight: '85vh',
              borderRadius: 10,
              border: '1px solid #dee2e6',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                padding: '12px 14px',
                borderBottom: '1px solid #dee2e6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div style={{ fontWeight: 700, wordBreak: 'break-all' }}>{rendered.key}</div>
              <button
                className="btn btn-sm btn-outline-secondary"
                type="button"
                onClick={() => setRenderModalOpen(false)}
              >
                Close
              </button>
            </div>

            <div style={{ padding: 14, overflow: 'auto', maxHeight: '60vh' }}>
              {rendered.truncated ? (
                <div className="alert alert-warning" style={{ marginBottom: 12 }}>
                  Preview truncated to {rendered.previewBytes} bytes of {rendered.totalBytes} bytes.
                </div>
              ) : null}
              <pre
                style={{
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'keep-all',
                  background: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: 6,
                  padding: 12,
                }}
              >
                {renderedPayload}
              </pre>
            </div>

            <div
              style={{
                padding: '10px 14px',
                borderTop: '1px solid #dee2e6',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              <button
                className="btn btn-outline-primary"
                type="button"
                onClick={() => void handleCopyPayload()}
              >
                Copy payload
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => setRenderModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default CacheManager
